import { useState, useEffect } from 'react';
import { APP_NAME } from '@/lib/brand';
import { useParams } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import PublicLandingAiBubble from '@/components/PublicLandingAiBubble';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Building2, MapPin, Phone, Mail, Globe, Loader2, ChevronRight, Home, Users, DollarSign, Send, ExternalLink, Image as ImageIcon } from 'lucide-react';
import { normalizePhone } from '@/lib/phoneUtils';

function fmt(n) { return n ? `$${Number(n).toLocaleString('es-MX')}` : '$0'; }

export default function DeveloperLandingPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDev, setSelectedDev] = useState(null);
  const [devDetail, setDevDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [contactDialog, setContactDialog] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [contactDevId, setContactDevId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/developer/${slug}`).then(r => setData(r.data))
      .catch(() => toast.error('Desarrollador no encontrado'))
      .finally(() => setLoading(false));
  }, [slug]);

  const openDetail = async (dev) => {
    setSelectedDev(dev);
    setDetailLoading(true);
    try {
      const r = await api.get(`/developer/${slug}/development/${dev.id}`);
      setDevDetail(r.data.development);
    } catch { toast.error('Error cargando detalle'); }
    finally { setDetailLoading(false); }
  };

  const openContact = (devId = '') => {
    setContactDevId(devId);
    setContactDialog(true);
  };

  const submitContact = async () => {
    if (!contactForm.name) { toast.error('Nombre requerido'); return; }
    if (!contactForm.email && !contactForm.phone) { toast.error('Email o telefono requerido'); return; }
    setSubmitting(true);
    try {
      const normalizedPhone = contactForm.phone ? normalizePhone(contactForm.phone, '52', 'default') : '';
      await api.post(`/developer/${slug}/contact`, { ...contactForm, phone: normalizedPhone, development_id: contactDevId });
      toast.success('Gracias! Un asesor te contactara pronto.');
      setContactDialog(false);
      setContactForm({ name: '', email: '', phone: '', message: '' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen bg-[#0a0e17] flex items-center justify-center"><Loader2 className="w-8 h-8 text-cyan-400 animate-spin" /></div>;
  if (!data) return <div className="min-h-screen bg-[#0a0e17] flex items-center justify-center text-white">Desarrollador no encontrado</div>;

  const { company, developments, wa_bubble_phone } = data;
  const pc = company.primary_color || '#0ea5e9';

  return (
    <div className="min-h-screen bg-[#0a0e17] text-white" data-testid="developer-landing">
      {/* Hero */}
      <header className="relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${pc}22, #0a0e17)` }}>
        <div className="max-w-6xl mx-auto px-4 py-16 sm:py-24 relative z-10">
          <div className="flex items-center gap-4 mb-6">
            {company.logo_url && <img src={company.logo_url} alt={company.name} className="h-14 w-14 rounded-xl object-cover border-2 border-white/10" />}
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" data-testid="dev-company-name">{company.name}</h1>
              <p className="text-sm text-white/60 mt-1">Desarrollador Inmobiliario</p>
            </div>
          </div>
          {(company.landing_headline || company.description) && (
            <p className="text-base sm:text-lg text-white/70 max-w-2xl leading-relaxed">
              {company.landing_headline || company.description}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-4 mt-8">
            <Button onClick={() => openContact()} className="text-sm font-semibold px-6" style={{ backgroundColor: pc }} data-testid="dev-contact-hero-btn">
              <Send className="w-4 h-4 mr-2" /> Solicitar Informacion
            </Button>
            {company.contact_phone && (
              <a href={`tel:${company.contact_phone}`} className="flex items-center gap-2 text-sm text-white/60 hover:text-white transition">
                <Phone className="w-4 h-4" /> {company.contact_phone}
              </a>
            )}
            {company.website && (
              <a href={company.website} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-white/60 hover:text-white transition">
                <Globe className="w-4 h-4" /> Sitio Web
              </a>
            )}
          </div>
        </div>
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'40\' height=\'40\' viewBox=\'0 0 40 40\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'%23fff\' fill-opacity=\'1\'%3E%3Cpath d=\'M0 0h20v20H0zM20 20h20v20H20z\'/%3E%3C/g%3E%3C/svg%3E")' }} />
      </header>

      {/* Developments Grid */}
      <section className="max-w-6xl mx-auto px-4 py-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold">Desarrollos Disponibles</h2>
            <p className="text-sm text-white/50 mt-1">{developments.length} proyecto{developments.length !== 1 ? 's' : ''}</p>
          </div>
        </div>

        {developments.length === 0 ? (
          <div className="text-center py-20">
            <Building2 className="w-16 h-16 text-white/10 mx-auto mb-4" />
            <p className="text-white/40">No hay desarrollos disponibles por ahora.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="developments-grid">
            {developments.map(dev => (
              <DevCard key={dev.id} dev={dev} pc={pc} onDetail={() => openDetail(dev)} onContact={() => openContact(dev.id)} />
            ))}
          </div>
        )}
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-8">
        <div className="max-w-6xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {company.logo_url && <img src={company.logo_url} alt="" className="h-8 w-8 rounded-lg object-cover" />}
            <span className="text-sm text-white/50">{company.name}</span>
          </div>
          <div className="flex items-center gap-4 text-xs text-white/40">
            {company.contact_email && <a href={`mailto:${company.contact_email}`} className="hover:text-white/70 flex items-center gap-1"><Mail className="w-3 h-3" /> {company.contact_email}</a>}
            {company.address && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {company.address}</span>}
          </div>
          <p className="text-[10px] text-white/30">Powered by {APP_NAME}</p>
        </div>
      </footer>

      {/* WA Bubble */}
      {wa_bubble_phone && (
        <a
          href={`https://wa.me/${wa_bubble_phone.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(`Hola! Me interesa conocer sus desarrollos inmobiliarios`)}`}
          target="_blank" rel="noreferrer"
          className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-110 transition-all group"
          data-testid="wa-bubble"
        >
          <svg viewBox="0 0 24 24" className="w-7 h-7 text-white fill-current">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-[8px] text-white font-bold flex items-center justify-center animate-pulse">1</span>
        </a>
      )}
      <PublicLandingAiBubble endpoint={`${process.env.REACT_APP_BACKEND_URL}/api/developer/${company.slug}/chat`} intro={`Hola, soy la IA de ${company.name}. Puedo ayudarte con disponibilidad, precios y desarrollos.`} placeholder="Pregunta por disponibilidad, precios o desarrollos..." />

      {/* Development Detail Dialog */}
      <Dialog open={!!selectedDev} onOpenChange={v => { if (!v) { setSelectedDev(null); setDevDetail(null); } }}>
        <DialogContent className="bg-[#0d1117] border-white/10 max-w-3xl max-h-[85vh] overflow-y-auto">
          {detailLoading ? (
            <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-cyan-400 animate-spin" /></div>
          ) : devDetail ? (
            <>
              <DialogHeader>
                <DialogTitle className="text-white text-lg flex items-center gap-2">
                  <Building2 className="w-5 h-5" style={{ color: pc }} /> {devDetail.name}
                </DialogTitle>
              </DialogHeader>
              {/* Gallery */}
              {devDetail.gallery?.length > 0 && (
                <div className="flex gap-2 overflow-x-auto pb-2 -mx-2 px-2">
                  {devDetail.gallery.slice(0, 5).map((img, i) => (
                    <img key={i} src={img} alt="" className="w-48 h-32 object-cover rounded-lg shrink-0 border border-white/10" />
                  ))}
                </div>
              )}
              {devDetail.cover_image && !devDetail.gallery?.length && (
                <img src={devDetail.cover_image} alt="" className="w-full h-48 object-cover rounded-lg border border-white/10" />
              )}
              <div className="space-y-4 mt-3">
                {devDetail.description && <p className="text-sm text-white/70">{devDetail.description}</p>}
                {devDetail.address && <p className="text-xs text-white/50 flex items-center gap-1"><MapPin className="w-3 h-3" /> {devDetail.address}</p>}

                {/* Unit Summary */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <StatBox label="Total Unidades" value={devDetail.units?.length || 0} color="cyan" />
                  <StatBox label="Disponibles" value={devDetail.units?.filter(u => u.status === 'disponible').length || 0} color="emerald" />
                  <StatBox label="Apartados" value={devDetail.units?.filter(u => u.status === 'apartado').length || 0} color="amber" />
                  <StatBox label="Vendidos" value={devDetail.units?.filter(u => u.status === 'vendido').length || 0} color="red" />
                </div>

                {/* Available Units */}
                {devDetail.units?.filter(u => u.status === 'disponible').length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-white mb-2">Unidades Disponibles</h4>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {devDetail.units.filter(u => u.status === 'disponible').map(u => (
                        <div key={u.id} className="p-3 rounded-lg bg-black/30 border border-white/5 flex items-center justify-between" data-testid={`unit-${u.id}`}>
                          <div>
                            <p className="text-sm text-white font-medium">{u.label || u.name || `Unidad ${u.number || u.id?.slice(-4)}`}</p>
                            <div className="flex items-center gap-3 text-[10px] text-white/50 mt-0.5">
                              {u.area && <span>{u.area} m²</span>}
                              {u.bedrooms && <span>{u.bedrooms} rec.</span>}
                              {u.bathrooms && <span>{u.bathrooms} ban.</span>}
                              {u.floor && <span>Piso {u.floor}</span>}
                            </div>
                          </div>
                          <div className="text-right">
                            {u.price ? <p className="text-sm font-bold" style={{ color: pc }}>{fmt(u.price)}</p> : null}
                            <Badge className="text-[9px] bg-emerald-500/10 text-emerald-400 border-emerald-500/20">Disponible</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Links */}
                <div className="flex flex-wrap gap-2">
                  {devDetail.gmaps_url && (
                    <a href={devDetail.gmaps_url} target="_blank" rel="noreferrer" className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1">
                      <ExternalLink className="w-3 h-3" /> Google Maps
                    </a>
                  )}
                  {devDetail.waze_url && (
                    <a href={devDetail.waze_url} target="_blank" rel="noreferrer" className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1">
                      <ExternalLink className="w-3 h-3" /> Waze
                    </a>
                  )}
                  {devDetail.virtual_tour_url && (
                    <a href={devDetail.virtual_tour_url} target="_blank" rel="noreferrer" className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1">
                      <ExternalLink className="w-3 h-3" /> Tour Virtual
                    </a>
                  )}
                  {devDetail.brochure_url && (
                    <a href={devDetail.brochure_url} target="_blank" rel="noreferrer" className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1">
                      <ExternalLink className="w-3 h-3" /> Brochure
                    </a>
                  )}
                </div>

                <Button onClick={() => { setSelectedDev(null); setDevDetail(null); openContact(devDetail.id); }} className="w-full text-sm" style={{ backgroundColor: pc }} data-testid="dev-detail-contact-btn">
                  <Send className="w-4 h-4 mr-2" /> Solicitar Informacion de {devDetail.name}
                </Button>
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Contact Dialog */}
      <Dialog open={contactDialog} onOpenChange={setContactDialog}>
        <DialogContent className="bg-[#0d1117] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2"><Send className="w-4 h-4" style={{ color: pc }} /> Solicitar Informacion</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input value={contactForm.name} onChange={e => setContactForm(f => ({ ...f, name: e.target.value }))} placeholder="Tu nombre *" className="bg-black/30 border-white/10 text-sm" data-testid="dev-contact-name" />
            <Input value={contactForm.email} onChange={e => setContactForm(f => ({ ...f, email: e.target.value }))} placeholder="Email" type="email" className="bg-black/30 border-white/10 text-sm" data-testid="dev-contact-email" />
            <Input value={contactForm.phone} onChange={e => setContactForm(f => ({ ...f, phone: e.target.value }))} placeholder="Telefono" className="bg-black/30 border-white/10 text-sm" data-testid="dev-contact-phone" />
            <textarea value={contactForm.message} onChange={e => setContactForm(f => ({ ...f, message: e.target.value }))} placeholder="Mensaje (opcional)" className="w-full min-h-[80px] p-3 rounded-md bg-black/30 border border-white/10 text-sm text-white resize-none focus:border-cyan-500/50 focus:outline-none" data-testid="dev-contact-message" />
            <Button onClick={submitContact} disabled={submitting} className="w-full text-sm" style={{ backgroundColor: pc }} data-testid="dev-contact-submit">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Enviar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DevCard({ dev, pc, onDetail, onContact }) {
  return (
    <Card className="bg-[#111827]/80 border-white/5 hover:border-white/15 transition-all overflow-hidden group cursor-pointer" onClick={onDetail} data-testid={`dev-card-${dev.id}`}>
      <div className="relative aspect-[16/10] bg-black/40 overflow-hidden">
        {dev.cover_image ? (
          <img src={dev.cover_image} alt={dev.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ImageIcon className="w-12 h-12 text-white/10" />
          </div>
        )}
        {dev.available_units > 0 && (
          <Badge className="absolute top-3 left-3 bg-emerald-500/80 text-white text-[10px] border-0">
            {dev.available_units} disponible{dev.available_units > 1 ? 's' : ''}
          </Badge>
        )}
        {dev.status === 'preventa' && (
          <Badge className="absolute top-3 right-3 bg-amber-500/80 text-white text-[10px] border-0">Preventa</Badge>
        )}
      </div>
      <CardContent className="p-4">
        <h3 className="text-base font-semibold text-white group-hover:text-cyan-300 transition-colors mb-1">{dev.name}</h3>
        {dev.address && <p className="text-xs text-white/40 flex items-center gap-1 mb-3"><MapPin className="w-3 h-3" /> {dev.address}</p>}

        <div className="flex items-center gap-3 text-[11px] text-white/60 mb-3">
          <span className="flex items-center gap-1"><Home className="w-3 h-3" /> {dev.total_units} unid.</span>
          {dev.available_units > 0 && <span className="text-emerald-400">{dev.available_units} disp.</span>}
          {dev.sold_units > 0 && <span className="text-red-400/60">{dev.sold_units} vendidos</span>}
        </div>

        {(dev.price_min > 0 || dev.price_max > 0) && (
          <p className="text-sm font-bold mb-3" style={{ color: pc }}>
            {dev.price_min === dev.price_max ? fmt(dev.price_min) : `${fmt(dev.price_min)} - ${fmt(dev.price_max)}`}
          </p>
        )}

        <div className="flex gap-2">
          <Button size="sm" className="flex-1 text-xs" style={{ backgroundColor: pc }} onClick={e => { e.stopPropagation(); onContact(); }} data-testid={`dev-contact-${dev.id}`}>
            <Send className="w-3 h-3 mr-1" /> Contactar
          </Button>
          <Button size="sm" variant="outline" className="text-xs border-white/10" onClick={e => { e.stopPropagation(); onDetail(); }} data-testid={`dev-detail-${dev.id}`}>
            Ver Detalle <ChevronRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function StatBox({ label, value, color }) {
  const colors = {
    cyan: 'text-cyan-400 bg-cyan-500/10 border-cyan-500/10',
    emerald: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/10',
    amber: 'text-amber-400 bg-amber-500/10 border-amber-500/10',
    red: 'text-red-400 bg-red-500/10 border-red-500/10',
  };
  return (
    <div className={`p-3 rounded-lg border ${colors[color]}`}>
      <p className="text-lg font-bold">{value}</p>
      <p className="text-[10px] text-white/50 uppercase tracking-wider">{label}</p>
    </div>
  );
}
